import { IntlShape, MessageDescriptor } from 'react-intl'

import { CountryCode, findPhoneNumbersInText, parsePhoneNumber } from 'libphonenumber-js'
import { isNil, startCase } from 'lodash-es'

import { ProviderLanguage as BCP47LanguageItem, LanguageItemForUIDisplay } from '../../../models/providers/Providers'
import { CARE_LANGUAGES } from '../../common/constants/constants'
import {
  CARELON_BASE_URL,
  DEMO_CARELON_CUSTOMER_NAME,
  DEMO_CUSTOMER_NAMES,
  SURVEYMONKEY_CUSTOMER_NAMES,
} from '../constants/workLifeConstants'

/**
 * Get the E.164 format a phone number to use, for example, in the href of phone number links.
 * @param {string} phoneNumber - String containing a phone number (e.g. '(844) 993-3322' or '(800) 333–4119 prompt 1')
 * @param {string} countryCode - Optional parameter that is a 2 letter ISO country code, defaults to 'US' if parameter is absent
 * @returns {string} The phone number in E.164 format (e.g. '+18449933322')
 */
export const getPhoneNumberInE164Format = ({
  phoneNumber,
  countryCode = 'US',
}: {
  phoneNumber: string
  countryCode?: CountryCode
}) => {
  const phoneNumbers = findPhoneNumbersInText(phoneNumber, countryCode)

  return phoneNumbers?.[0]?.number?.number
}

/**
 * Formats a phone number string for display e.g. 3108484848 will become (310) 848-4848
 */
export const formatPhoneNumberForDisplay = (phoneNumber?: string, countryCode: CountryCode = 'US') => {
  try {
    // Vanity numbers (e.g. "855-3MY-AMEX") contain letters and should be displayed as-is
    if (phoneNumber && /[a-zA-Z]/.test(phoneNumber)) {
      return phoneNumber
    }
    const parsedPhoneNumber = !isNil(phoneNumber) ? parsePhoneNumber(phoneNumber, countryCode) : phoneNumber
    if (parsedPhoneNumber) {
      return countryCode !== 'US' ? parsedPhoneNumber.formatInternational() : parsedPhoneNumber.formatNational()
    }
    return parsedPhoneNumber
  } catch {
    return phoneNumber
  }
}

/**
 *
 * @param inputString - e.g. 'internationaleap@lyrahealth.com' or 'internationaleap@lyrahealth.com (French)'
 * @returns the email address found in `inputString`, e.g. getEmailAddressFromString('internationaleap@lyrahealth.com (French)') returns 'internationaleap@lyrahealth.com',
 * if no email is found, this function returns undefined
 */
export const getEmailAddressFromString = (inputString: string) => {
  const emailRegex =
    // oxlint-disable-next-line no-control-regex -- This RFC 5322 email pattern intentionally permits control characters in quoted local parts.
    /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/
  const emails = inputString.match(emailRegex)
  return emails?.[0]
}

export const getContactUrl = ({
  phoneNumberOrEmail,
  countryCode,
}: {
  phoneNumberOrEmail: string
  countryCode?: CountryCode
}) => {
  const email = getEmailAddressFromString(phoneNumberOrEmail)
  if (email) {
    return `mailto:${email}`
  }
  return `tel:${getPhoneNumberInE164Format({ phoneNumber: phoneNumberOrEmail, countryCode }) ?? phoneNumberOrEmail}`
}

/**
 * @param inputString - e.g. 'internationaleap@lyrahealth.com' or 'internationaleap@lyrahealth.com (French)'
 * @returns the email client found in `inputString`, e.g. getEmailAddressClientFromString('testEmail@gmail.com') returns 'gmail',
 * if no email client is found, this function returns undefined
 */
export const getEmailAddressClientFromString = (inputString: string) => {
  const emailRegex = /.+@([\w]+)\..+/
  const emails = inputString.match(emailRegex)
  return emails?.[1]
}

/**
 * Cleans up an email address input. Removes any leading and trailing spaces.
 * @param inputString - e.g. '   test@email.com   '
 * @returns - test@email.com
 */
export const sanitizeEmailAddressInput = (inputString: string | undefined) => {
  return inputString ? inputString.trim() : undefined
}

/**
 * Given the supplied country return the di
 */
export const getCountryDisplayName = (countryCode: string) => {
  const countryName = new Intl.DisplayNames(['en'], { type: 'region' }).of(countryCode)

  if (!countryName) {
    return
  }

  const countriesWithDefiniteArticle = [
    'BAHAMAS',
    'CAYMAN ISLANDS',
    'CENTRAL AFRICAN REPUBLIC',
    'CHANNEL ISLANDS',
    'COMOROS',
    'CHECH REPUBLIC',
    'DOMINICAN REPUBLIC',
    'FALKLAND ISLANDS',
    'GAMBIA',
    'ISLE OF MAN',
    'IVORY COAST',
    'LEEWARD ISLANDS',
    'MALDIVES',
    'MARSHALL ISLANDS',
    'NETHERLANDS',
    'NETHERLANDS ANTILLES',
    'PHILIPPINES',
    'SOLOMON ISLANDS',
    'TURKS AND CAICOS ISLANDS',
    'UNITED ARAB EMIRATES',
    'UNITED KINGDOM',
    'UNITED STATES',
    'VIRGIN ISLANDS',
  ]
  return countryName && countriesWithDefiniteArticle.includes(countryName.toUpperCase())
    ? `The ${countryName}`
    : countryName
}

export const getInitials = (name: string) => {
  let letters = name.match(/\b\w/g) || []
  const titleIndex = name.indexOf(',')
  if (titleIndex > -1) {
    letters = name.slice(0, titleIndex).match(/\b\w/g) || []
  }
  return ((letters.shift() || '') + (letters.pop() || '')).toUpperCase()
}

export const articlize = (company = 'a') => {
  const letter = company.charAt(0).toLowerCase()
  let text = 'a'
  if (['a', 'e', 'i', 'o', 'u'].includes(letter)) {
    text = 'an'
  }
  return text
}

/**
 * Used for modality preferences to convert answers to int for the payload
 */
export const getStringIndexAsInt = (index: string, zeroBased?: boolean) => {
  const int = parseInt(index, 10)
  return zeroBased ? int - 1 : int
}

export const objectifyBCP47LanguageItems = ({
  languageItems = [],
  intl,
  tagOfSelectedLanguage,
}: {
  languageItems: BCP47LanguageItem[]
  intl: IntlShape
  tagOfSelectedLanguage?: string
}): LanguageItemForUIDisplay[] =>
  languageItems
    .map((language) => {
      const careLanguage = (CARE_LANGUAGES as Record<string, MessageDescriptor | undefined>)[language?.tag]
      if (!careLanguage) {
        return null
      }
      return {
        name: intl.formatMessage(careLanguage),
        id: language.tag, // e.g. 'fr', 'de', 'en'
        isSelected: tagOfSelectedLanguage === language.tag,
      } as LanguageItemForUIDisplay
    })
    .filter((lang): lang is LanguageItemForUIDisplay => lang !== null)

/**
 * Takes a delimited string and applies `startCase` to each segment
 * @param inputString
 * @param delimiter delimiter used in the input string
 * @param joinDelimiter delimiter to use for the output string
 */
export const startCaseDelimitedString = (inputString: string, joinDelimiter = ', ', delimiter = ',') => {
  return inputString
    .split(delimiter)
    .filter((element) => element.length > 0)
    .map((element) => startCase(element))
    .join(joinDelimiter)
}

export const getWorkLifeServiceCode = function (customerName: string, workLifeServiceDependentCareCode?: string) {
  return workLifeServiceDependentCareCode ? workLifeServiceDependentCareCode : `Lyra-${customerName}`
}

/**
 * Generates the Carelon work life services URL for a given customer.
 * Handles the special case of SurveyMonkey/Momentive customers and demo customers.
 *
 * @param {string} customerName - The customer name
 * @returns {string} The Carelon URL for the customer
 */
export const getCarelonUrl = (customerName: string): string => {
  let carelonCustomerName = customerName

  if (SURVEYMONKEY_CUSTOMER_NAMES.includes(customerName)) {
    carelonCustomerName = 'surveymonkey'
  } else if (DEMO_CUSTOMER_NAMES.includes(customerName)) {
    carelonCustomerName = DEMO_CARELON_CUSTOMER_NAME
  }

  return `${CARELON_BASE_URL}/${carelonCustomerName}`
}

/**
 * Taken from ui-core. Customer phone number should be a US number.
 * Formats a string consisting of digits as a phone number of format +1 (xxx) yyy-zzzz,
 * with the international calling code +1 only included if the phone number already includes it.
 * Can only handle strings of 10+ digits long; will return the unmodified string otherwise.
 */
export const formatCustomerPhoneNumber = function (phone = '') {
  phone = phone?.replace(/\D/g, '')
  if (phone?.length < 10) {
    console.warn(`This phone number is missing digits.
    Please reach out to the appropriate person for the correct phone number.`)
    return phone
  }
  const internationalAccessCode = phone.charAt(0) === '1' ? '+1 ' : ''
  const domesticPhoneNumber = internationalAccessCode.length > 0 ? phone.substring(1) : phone
  const areaCode = domesticPhoneNumber.substring(0, 3)
  const centralOfficeCode = domesticPhoneNumber.substring(3, 6)
  const subscriberNumber = domesticPhoneNumber.substring(6, 10)
  const extension = domesticPhoneNumber.length > 10 ? ' ext. ' + domesticPhoneNumber.substring(10) : ''

  return `${internationalAccessCode}(${areaCode}) ${centralOfficeCode}-${subscriberNumber}${extension}`
}

/**
 * @param num number
 * @returns the number with an ordinal indicator added to the end, such as '1st', '2nd', '15th', etc
 * Ref: https://gist.github.com/jlbruno/1535691/db35b4f3af3dcbb42babc01541410f291a8e8fac
 */
export const addOrdinalSuffix = function (num: number) {
  const ordinalStrings = ['th', 'st', 'nd', 'rd']
  const doubleDigit = num % 100
  return num + (ordinalStrings[(doubleDigit - 20) % 10] || ordinalStrings[doubleDigit] || ordinalStrings[0])
}

// Determines whether user is using Apple feature for private relay email
export const isPrivateRelayEmail = ({ email }: { email?: string }) => {
  return Boolean(email && email.includes('@privaterelay.appleid.com'))
}

export const numberToOrdinalWord = (num: number | undefined | null) => {
  if (!Number.isInteger(num) || !num) {
    return 'first'
  }

  // Word forms for numbers 1-9
  const ordinalWords: Record<number, string> = {
    1: 'first',
    2: 'second',
    3: 'third',
    4: 'fourth',
    5: 'fifth',
    6: 'sixth',
    7: 'seventh',
    8: 'eighth',
    9: 'ninth',
  }

  // For numbers 1-9, return word form
  if (num <= 9) {
    return ordinalWords[num]
  }

  return addOrdinalSuffix(num)
}

// Common multi-part TLD suffixes (country-code second-level domains)
const MULTI_PART_TLD_SUFFIXES = ['.co.uk', '.com.au', '.org.uk', '.net.au', '.com.br', '.co.nz', '.co.jp', '.co.in']

/**
 * Extracts the TLD from a domain, handling multi-part TLDs like .co.uk, .com.au
 */
const extractTld = (domain: string): { tld: string; domainName: string } => {
  const lowerDomain = domain.toLowerCase()
  for (const suffix of MULTI_PART_TLD_SUFFIXES) {
    if (lowerDomain.endsWith(suffix)) {
      const domainName = domain.slice(0, domain.length - suffix.length)
      const tld = domain.slice(domain.length - suffix.length)
      return { tld, domainName }
    }
  }
  const lastDotIndex = domain.lastIndexOf('.')
  if (lastDotIndex === -1) {
    return { tld: '', domainName: domain }
  }
  return { tld: domain.slice(lastDotIndex), domainName: domain.slice(0, lastDotIndex) }
}

export const truncateEmail = (
  emailAddress: string,
  maxLength: number = 28,
  truncateDomain: boolean = false,
): string => {
  if (emailAddress.length <= maxLength) {
    return emailAddress
  }
  const [localPart, domain] = emailAddress.split('@')
  if (!domain) {
    return truncateDomain && emailAddress.length > maxLength
      ? emailAddress.slice(0, maxLength - 3) + '...'
      : emailAddress
  }

  if (!truncateDomain) {
    // Allow local part to be truncated to as little as 1 character to save space
    const availableLength = maxLength - domain.length - 4 // 4 = @ + ...
    if (availableLength < 1) {
      return `...@${domain}`
    }

    // For very short available space (1-2 chars), just use start chars
    if (availableLength <= 2) {
      return `${localPart.slice(0, availableLength)}...@${domain}`
    }

    const keepStart = Math.ceil(availableLength / 2)
    const keepEnd = Math.floor(availableLength / 2)

    return `${localPart.slice(0, keepStart)}...${localPart.slice(-keepEnd)}@${domain}`
  }

  // Truncate domain mode: also shorten the domain while preserving TLD
  const { tld, domainName } = extractTld(domain)

  // Reserve space for @, tld, and ellipsis
  const reservedChars = 1 + tld.length + 3 // @ + .tld + ...
  const availableForContent = maxLength - reservedChars

  if (availableForContent <= 0) {
    return '...' + tld
  }

  // Split available space between local part and domain name
  const localSpace = Math.ceil(availableForContent * 0.6)
  const domainSpace = Math.floor(availableForContent * 0.4)

  const truncatedLocal = localPart.length > localSpace ? localPart.slice(0, localSpace) + '...' : localPart
  const truncatedDomain =
    domainName.length > domainSpace ? domainName.slice(0, Math.max(domainSpace - 2, 1)) + '..' : domainName

  return `${truncatedLocal}@${truncatedDomain}${tld}`
}

/**
 * Checks if a card last 4 digits value exists (is non-null, non-undefined, and non-empty).
 * Used to determine if a payment method has a card on file.
 *
 * @param cardLast4 - The last 4 digits of a card (can be string, null, or undefined)
 * @returns true if the card last 4 value exists, false otherwise
 *
 * @example
 * hasCardLast4('1234') // true
 * hasCardLast4('') // false
 * hasCardLast4(null) // false
 * hasCardLast4(undefined) // false
 */
export const hasCardLast4 = (cardLast4: string | null | undefined): boolean => {
  return cardLast4 != null && cardLast4 !== ''
}

/**
 * Formats a number with optional decimal places, only showing decimals if they exist.
 * @param value - The number to format
 * @param isPercentage - If true, multiplies by 100 before formatting (default: false)
 * @param decimalPlaces - Number of decimal places to round to (default: 1)
 * @returns Formatted string, with '%' suffix if isPercentage is true
 *
 * @example
 * formatNumberToDecimalPlaces({ value: 0.856, isPercentage: true }) // '85.6%'
 * formatNumberToDecimalPlaces({ value: 0.9, isPercentage: true }) // '90%'
 * formatNumberToDecimalPlaces({ value: 8.9 }) // '8.9'
 * formatNumberToDecimalPlaces({ value: 8 }) // '8'
 * formatNumberToDecimalPlaces({ value: 8.123, decimalPlaces: 2 }) // '8.12'
 */
export const formatNumberToDecimalPlaces = ({
  value,
  isPercentage = false,
  decimalPlaces = 1,
}: {
  value: number
  isPercentage?: boolean
  decimalPlaces?: number
}): string => {
  const num = isPercentage ? value * 100 : value
  const roundedToHundredths = Math.round(num * 100) / 100
  const hasDecimals = roundedToHundredths % 1 !== 0
  const formatted = hasDecimals
    ? roundedToHundredths.toFixed(decimalPlaces)
    : Math.round(roundedToHundredths).toString()
  return isPercentage ? `${formatted}%` : formatted
}
